/**
 * Session event contract in the browser (cross-cutting domain).
 * The HTTP adapter only fires the event; the shell listens without depending on that adapter.
 */
export const AUTH_SESSION_EXPIRED_EVENT = 'auth:session-expired' as const;

/** Fired after a successful in-app login so the shell can mount layout + router without reloading. */
export const SHELL_AUTH_LOGIN_SUCCESS_EVENT = 'shell:auth-login-success' as const;

/** Catalog sync overlay: layout shows blocking loader while true. */
export const SHELL_CATALOG_SYNC_BUSY_EVENT = 'shell:catalog-sync-busy' as const;
export const SHELL_CATALOG_SYNC_IDLE_EVENT = 'shell:catalog-sync-idle' as const;

export function emitCatalogSyncBusy(): void {
  try {
    globalThis.dispatchEvent(new CustomEvent(SHELL_CATALOG_SYNC_BUSY_EVENT));
  } catch {
    /* ignore */
  }
}

export function emitCatalogSyncIdle(): void {
  try {
    globalThis.dispatchEvent(new CustomEvent(SHELL_CATALOG_SYNC_IDLE_EVENT));
  } catch {
    /* ignore */
  }
}

/** Fired when `#app` layout receives updated `shellPorts` without remount. */
export const SHELL_PORTS_UPDATED_EVENT = 'shell:ports-updated' as const;

/** Pending entrega/reposición queue changed — layout refreshes nav badge. */
export const SHELL_PENDING_SYNC_CHANGED_EVENT = 'shell:pending-sync-changed' as const;

export function emitShellPendingSyncChanged(): void {
  try {
    globalThis.dispatchEvent(new CustomEvent(SHELL_PENDING_SYNC_CHANGED_EVENT));
  } catch {
    /* ignore */
  }
}

/** Set by hard refresh after storage wipe; triggers catalog pull on `/login` before credentials. */
export const PENDING_CATALOG_SYNC_AFTER_UPDATE_KEY = 'esan:pending-catalog-sync-after-update' as const;

export function markPendingCatalogSyncAfterUpdate(): void {
  try {
    globalThis.sessionStorage?.setItem(PENDING_CATALOG_SYNC_AFTER_UPDATE_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isPendingCatalogSyncAfterUpdate(): boolean {
  try {
    return globalThis.sessionStorage?.getItem(PENDING_CATALOG_SYNC_AFTER_UPDATE_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearPendingCatalogSyncAfterUpdate(): void {
  try {
    globalThis.sessionStorage?.removeItem(PENDING_CATALOG_SYNC_AFTER_UPDATE_KEY);
  } catch {
    /* ignore */
  }
}

/** Sync page → feature form: open edit modal after navigation. */
export const PENDING_EDIT_ENTREGA_KEY = 'esan:pending-edit-entrega' as const;
export const PENDING_EDIT_REPOSICION_KEY = 'esan:pending-edit-reposicion' as const;

export const SHELL_PAGE_ACTIVATED_EVENT = 'shell:page-activated' as const;

export function markPendingEditEntrega(publicId: string): void {
  try {
    globalThis.sessionStorage?.setItem(PENDING_EDIT_ENTREGA_KEY, publicId);
  } catch {
    /* ignore */
  }
}

export function markPendingEditReposicion(publicId: string): void {
  try {
    globalThis.sessionStorage?.setItem(PENDING_EDIT_REPOSICION_KEY, publicId);
  } catch {
    /* ignore */
  }
}

export function consumePendingEditEntrega(): string | null {
  try {
    const publicId = globalThis.sessionStorage?.getItem(PENDING_EDIT_ENTREGA_KEY);
    if (publicId) globalThis.sessionStorage?.removeItem(PENDING_EDIT_ENTREGA_KEY);
    return publicId || null;
  } catch {
    return null;
  }
}

export function consumePendingEditReposicion(): string | null {
  try {
    const publicId = globalThis.sessionStorage?.getItem(PENDING_EDIT_REPOSICION_KEY);
    if (publicId) globalThis.sessionStorage?.removeItem(PENDING_EDIT_REPOSICION_KEY);
    return publicId || null;
  } catch {
    return null;
  }
}

/** Clears session via shell listener ({@link installSessionExpiredListener}). */
export function emitAuthSessionExpired(): void {
  try {
    globalThis.dispatchEvent(new CustomEvent(AUTH_SESSION_EXPIRED_EVENT));
  } catch {
    /* A faulty listener must not swallow the caller error path. */
  }
}
